import type { AbaloneMove, AbaloneState } from '../../games/abalone';
import { AbaloneBoard } from './AbaloneBoard';
import type { ChuShogiMove, ChuShogiState } from '../../games/chu-shogi';
import { ChuShogiBoard } from './ChuShogiBoard';
import type { KyoenMove, KyoenState } from '../../games/kyoen';
import { KyoenBoard } from './KyoenBoard';
import type { GomokuMove, GomokuState } from '../../games/gomoku';
import { GomokuBoard } from './GomokuBoard';
import type { BridgeNtMove, BridgeNtState } from '../../games/bridge-nt';
import { BridgeNtBoard } from './BridgeNtBoard';
import type { TraxMove, TraxState } from '../../games/trax';
import { TraxBoard } from './TraxBoard';
import type { AmazonsMove, AmazonsState } from '../../games/amazons';
import { AmazonsBoard } from './AmazonsBoard';
/**
 * BoardRenderer: BoardEntry.game_id で 7 種の盤コンポーネントに dispatch する単一ポイント。
 *
 * 旧 Play.tsx / Result.tsx / BoardThumb に 3 度繰り返されていた if-else 連鎖を、 ここに集約。
 * ゲームを追加するときの編集箇所はこの 1 ファイルだけにする。
 */

import type { AnimalMove, AnimalState } from '../../games/animal';
import type { BgArrow, BgMove, BgState } from '../../games/backgammon';
import type { ChessMove, ChessState } from '../../games/chess';
import type { Connect6Move, Connect6State } from '../../games/connect6';
import type { DraughtsMove, DraughtsState } from '../../games/draughts';
import type { PokerMatchState, PokerMove } from '../../games/heads-up-poker';
import type { JanggiMove, JanggiState } from '../../games/janggi';
import type { JungoMove, JungoState } from '../../games/jungo';
import type { KalahMove, KalahState } from '../../games/kalah';
import type { RenjuMove, RenjuState } from '../../games/renju';
import type { ReversiMove, ReversiState } from '../../games/reversi';
import type { ShogiMove, ShogiState } from '../../games/shogi';
import type { TwixtMove, TwixtState } from '../../games/twixt';
import type { XiangqiMove, XiangqiState } from '../../games/xiangqi';
import type { BoardEntry, Side } from '../../lib/match-snapshot';
import { AnimalBoard } from './AnimalBoard';
import { BackgammonBoard } from './BackgammonBoard';
import { ChessBoard } from './ChessBoard';
import { Connect6Board } from './Connect6Board';
import { DraughtsBoard } from './DraughtsBoard';
import { HeadsUpPokerBoard } from './HeadsUpPokerBoard';
import { JanggiBoard } from './JanggiBoard';
import { JungoBoard } from './JungoBoard';
import { KalahBoard } from './KalahBoard';
import { RenjuBoard } from './RenjuBoard';
import { ReversiBoard } from './ReversiBoard';
import { ShogiBoard } from './ShogiBoard';
import { TwixtBoard } from './TwixtBoard';
import { XiangqiBoard } from './XiangqiBoard';

interface FullProps {
  entry: BoardEntry;
  legal: unknown[] | undefined;
  yourSide: Side | null;
  myTurnAllowed: boolean;
  onMove: (mv: unknown) => void;
  lastMove: unknown;
  /** 直近の 1 つ前の手 (Connect6 の 1 ターン 2 石ハイライト用)。 他ゲームは未使用。 */
  prevMove?: unknown;
  mini?: false;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を抑制する。 Play 中は false で OK。 */
  staticView?: boolean;
  suppressTurnStatus?: boolean;
  /** 盤の向きを手番と切り離す。 検討では手番が変わっても盤を回さない。 */
  viewFrom?: Side;
  /** Backgammon の盤に重ねる矢印。 */
  arrows?: BgArrow[];
}

interface MiniProps {
  entry: BoardEntry;
  yourSide: Side | null;
  mini: true;
}

export function BoardRenderer(props: FullProps | MiniProps): JSX.Element {
  const { entry, yourSide } = props;
  const state = entry.state;

  if (props.mini) {
    // mini でも yourSide は必ず渡す。 落とすと **その盤だけ相手視点で描かれる**
    // (チェスの縮小盤が、 自分が黒でも白を手前にしたまま = 「いつも先手」 に見える)。
    switch (entry.game_id) {
      case 'Abalone':
        return <AbaloneBoard state={state as AbaloneState} mini yourSide={yourSide} />;
      case 'ChuShogi':
        return <ChuShogiBoard state={state as ChuShogiState} mini yourSide={yourSide} />;
      case 'Kyoen':
        return <KyoenBoard state={state as KyoenState} mini yourSide={yourSide} />;
      case 'Gomoku':
        return <GomokuBoard state={state as GomokuState} mini yourSide={yourSide} />;
      case 'BridgeNT':
        return <BridgeNtBoard state={state as BridgeNtState} mini yourSide={yourSide} />;
      case 'Trax':
        return <TraxBoard state={state as TraxState} mini yourSide={yourSide} />;
      case 'Amazons':
        return <AmazonsBoard state={state as AmazonsState} mini yourSide={yourSide} />;
      case 'Reversi':
        return <ReversiBoard state={state as ReversiState} mini yourSide={yourSide} />;
      case 'Backgammon':
        return <BackgammonBoard state={state as BgState} mini yourSide={yourSide} />;
      case 'Renju':
        return <RenjuBoard state={state as RenjuState} mini yourSide={yourSide} />;
      case 'Chess':
        return <ChessBoard state={state as ChessState} mini yourSide={yourSide} />;
      case 'Shogi':
        return <ShogiBoard state={state as ShogiState} mini yourSide={yourSide} />;
      case 'Jungo':
        return <JungoBoard state={state as JungoState} mini yourSide={yourSide} />;
      case 'Connect6':
        return <Connect6Board state={state as Connect6State} mini yourSide={yourSide} />;
      case 'Kalah':
        return <KalahBoard state={state as KalahState} mini yourSide={yourSide} />;
      case 'TwixT':
        return <TwixtBoard state={state as TwixtState} mini yourSide={yourSide} />;
      case 'Xiangqi':
        return <XiangqiBoard state={state as XiangqiState} mini yourSide={yourSide} />;
      case 'Janggi':
        return <JanggiBoard state={state as JanggiState} mini yourSide={yourSide} />;
      case 'Draughts':
        return <DraughtsBoard state={state as DraughtsState} mini yourSide={yourSide} />;
      case 'HeadsUpPoker':
        return <HeadsUpPokerBoard state={state as PokerMatchState} mini yourSide={yourSide} />;
      default:
        return <AnimalBoard state={state as AnimalState} mini yourSide={yourSide} />;
    }
  }

  const { legal, myTurnAllowed, onMove, lastMove, prevMove, staticView, suppressTurnStatus, viewFrom, arrows } = props;
  switch (entry.game_id) {
    case 'Abalone':
      return <AbaloneBoard state={state as AbaloneState} legal={legal as AbaloneMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as AbaloneMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'ChuShogi':
      return <ChuShogiBoard state={state as ChuShogiState} legal={legal as ChuShogiMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as ChuShogiMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'Kyoen':
      return <KyoenBoard state={state as KyoenState} legal={legal as KyoenMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as KyoenMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'Gomoku':
      return <GomokuBoard state={state as GomokuState} legal={legal as GomokuMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as GomokuMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'BridgeNT':
      return <BridgeNtBoard state={state as BridgeNtState} legal={legal as BridgeNtMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as BridgeNtMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'Trax':
      return <TraxBoard state={state as TraxState} legal={legal as TraxMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as TraxMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'Amazons':
      return <AmazonsBoard state={state as AmazonsState} legal={legal as AmazonsMove[] | undefined} yourSide={yourSide} myTurnAllowed={myTurnAllowed} onMove={onMove} lastMove={lastMove as AmazonsMove | undefined} staticView={staticView} suppressTurnStatus={suppressTurnStatus} />;
    case 'Reversi':
      return (
        <ReversiBoard
          state={state as ReversiState}
          legal={legal as ReversiMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as ReversiMove | undefined}
          staticView={staticView}
        />
      );
    case 'AnimalShogi':
      return (
        <AnimalBoard
          viewFrom={viewFrom}
          state={state as AnimalState}
          legal={legal as AnimalMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as AnimalMove | undefined}
          staticView={staticView}
        />
      );
    case 'Backgammon':
      return (
        <BackgammonBoard
          arrows={arrows}
          board={entry.board}
          state={state as BgState}
          legal={legal as BgMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as BgMove | undefined}
          staticView={staticView}
        />
      );
    case 'Renju':
      return (
        <RenjuBoard
          state={state as RenjuState}
          hideOwnSide={suppressTurnStatus}
          legal={legal as RenjuMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as RenjuMove | undefined}
          staticView={staticView}
        />
      );
    case 'Chess':
      return (
        <ChessBoard
          state={state as ChessState}
          legal={legal as ChessMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as ChessMove | undefined}
          staticView={staticView}
        />
      );
    case 'Shogi':
      return (
        <ShogiBoard
          viewFrom={viewFrom}
          state={state as ShogiState}
          legal={legal as ShogiMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as ShogiMove | undefined}
          staticView={staticView}
        />
      );
    case 'Jungo':
      return (
        <JungoBoard
          state={state as JungoState}
          hideOwnSide={suppressTurnStatus}
          legal={legal as JungoMove[] | undefined}
          passAlive={(state as JungoState).pass_alive}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as JungoMove | undefined}
          staticView={staticView}
        />
      );
    case 'Connect6':
      return (
        <Connect6Board
          state={state as Connect6State}
          hideOwnSide={suppressTurnStatus}
          legal={legal as Connect6Move[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as Connect6Move | undefined}
          prevMove={prevMove as Connect6Move | undefined}
          staticView={staticView}
        />
      );
    case 'Kalah':
      return (
        <KalahBoard
          state={state as KalahState}
          legal={legal as KalahMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as KalahMove | undefined}
          staticView={staticView}
        />
      );
    case 'TwixT':
      return (
        <TwixtBoard
          state={state as TwixtState}
          legal={legal as TwixtMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as TwixtMove | undefined}
          staticView={staticView}
        />
      );
    case 'Xiangqi':
      return (
        <XiangqiBoard
          state={state as XiangqiState}
          legal={legal as XiangqiMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as XiangqiMove | undefined}
          staticView={staticView}
        />
      );
    case 'Janggi':
      return (
        <JanggiBoard
          state={state as JanggiState}
          legal={legal as JanggiMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as JanggiMove | undefined}
          staticView={staticView}
        />
      );
    case 'Draughts':
      return (
        <DraughtsBoard
          state={state as DraughtsState}
          legal={legal as DraughtsMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as DraughtsMove | undefined}
          staticView={staticView}
        />
      );
    case 'HeadsUpPoker':
      return (
        <HeadsUpPokerBoard
          state={state as PokerMatchState}
          legal={legal as PokerMove[] | undefined}
          yourSide={yourSide}
          myTurnAllowed={myTurnAllowed}
          onMove={(mv) => onMove(mv)}
          lastMove={lastMove as PokerMove | undefined}
          staticView={staticView}
          board={entry.board}
        />
      );
    default: {
      const _exhaustive: never = entry.game_id;
      return _exhaustive;
    }
  }
}
